import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useStore } from '@/hooks/useStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { storePath, getSubdomain } from "../../utils/storeUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreResetPassword() {
    const { subdomain: paramSubdomain } = useParams();
    const subdomain = paramSubdomain || getSubdomain();
    const { store, theme } = useStore(subdomain);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const store_id = searchParams.get('store_id');

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        if (token && store_id) {
            axios.get(`${API}/customers/validate-reset-token?token=${token}&store_id=${store_id}`)
                .then(() => setTokenValid(true))
                .catch(() => setTokenValid(false))
                .finally(() => setValidating(false));
        } else {
            setValidating(false);
        }
    }, [token, store_id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error("Passwords do not match");
            return;
        }
        
        if (password.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/customers/reset-password`, { token, store_id, password });
            setSuccess(true);
            toast.success("Password reset successful!");
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to reset password");
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-amber-700 border-t-transparent rounded-full animate-spin" style={{ borderTopColor: theme?.primary_color }}></div>
            </div>
        );
    }

    return (
        <div className="flex-1 flex items-center justify-center p-4">
            <div className="w-full max-w-md bg-white p-8 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100">
                <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold text-slate-900">
                        {success ? "Password Reset!" : !token || !tokenValid ? "Invalid Link" : "Set New Password"}
                    </h1>
                </div>

                {success ? (
                    <div className="text-center space-y-6">
                        <p className="text-slate-600">Your password has been reset successfully. You can now log in.</p>
                        <Button
                            onClick={() => navigate(storePath(subdomain, '/login'))}
                            className="w-full h-11 rounded-xl font-bold text-white"
                            style={{ backgroundColor: theme?.primary_color }}
                        >
                            Go to Login
                        </Button>
                    </div>
                ) : !token || !tokenValid ? (
                    <div className="text-center space-y-6">
                        <p className="text-slate-600">This password reset link is invalid or has expired.</p>
                        <Button
                            onClick={() => navigate(storePath(subdomain, '/forgot-password'))}
                            className="w-full h-11 rounded-xl font-bold text-white"
                            style={{ backgroundColor: theme?.primary_color }}
                        >
                            Request New Link
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label>New Password</Label>
                            <Input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                minLength={6}
                                className="h-11 rounded-xl"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Confirm Password</Label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                minLength={6}
                                className="h-11 rounded-xl"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full h-11 rounded-xl font-bold text-white"
                            style={{ backgroundColor: theme?.primary_color }}
                        >
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
